import { createContext, useContext } from "react";
import { createMongoAbility, type MongoAbility } from "@casl/ability";
import { createContextualCan } from "@casl/react";
import type { OrganizationRoleType } from "@/types/graphql/partner";

/**
 * CASL на фронте кабинета партнёра (спека:
 * docs/superpowers/specs/2026-09-06-organization-roles-design.md,
 * «Кабинет партнёра → CASL на фронте»). Субъекты — те же строки, что на
 * бэкенде (`Content`, `Product`, ...), действия
 * `read | create | update | delete | manage`. Правила приходят с бэкенда
 * упакованными (`User.abilityRules`, `packRules`), фронт их только
 * распаковывает — единый источник правил, дублирования таблицы ролей на
 * фронте нет (кроме различия video/streams, см. `hasRole`).
 *
 * Файл лежит в `shared`, а не в `entities/auth` (где по брифу физически
 * должен быть `AbilityProvider`), потому что `RequireAbility`
 * (`shared/ui/RequireAbility`) не может импортировать `entities`
 * (eslint-plugin-boundaries, см. комментарий про `LanguageField` в
 * `shared/ui/index.ts`), а ему нужны и `useAbility`/`Can`, и `hasRole`.
 * `entities/auth/contexts/AbilityProvider.tsx` наполняет эти примитивы
 * данными пользователя (`user.abilityRules`, `user.currentOrganizationRoles`).
 */
export type Action = "read" | "create" | "update" | "delete" | "manage";

export type AppAbility = MongoAbility<[Action, string]>;

export const AbilityContext = createContext<AppAbility>(createMongoAbility([]));

export const useAbility = (): AppAbility => useContext(AbilityContext);

// createContextualCan ожидает Consumer контекста, значение которого — сам
// ability (а не обёртка), поэтому роли сотрудника (нужны для различия
// video/streams) живут в отдельном контексте ниже, а не здесь.
export const Can = createContextualCan(AbilityContext.Consumer);

export const CurrentOrganizationRolesContext = createContext<
  OrganizationRoleType[]
>([]);

export const useCurrentOrganizationRoles = (): OrganizationRoleType[] =>
  useContext(CurrentOrganizationRolesContext);

/**
 * Видео и трансляции используют один субъект (`Content`), различаются
 * условием по `type`, которое CASL без экземпляра не проверяет
 * (`ability.can("read", "Content")` истинно для обеих ролей). Это
 * единственное место, где фронт смотрит на роли сотрудника напрямую, а не
 * на ability (см. спеку, раздел «Видео и трансляции»).
 */
export function hasRole(
  user: { currentOrganizationRoles?: OrganizationRoleType[] | null },
  ...roles: OrganizationRoleType[]
): boolean {
  const userRoles = user.currentOrganizationRoles ?? [];
  return roles.some((role) => userRoles.includes(role));
}
